<template>
  <v-dialog v-model="dialog" width="960px">
    <v-card>
      <v-card-title class="mx-3">
        {{ $t("CRUD.chooseInfo", [$t("PurchasesDetail.ObjectName")]) }}
        <v-spacer />
        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" @click="dialog = false">
              fa-times
            </v-icon>
          </template>
          <span>{{ $t("$vuetify.close") }}</span>
        </v-tooltip>
      </v-card-title>
      <v-divider></v-divider>
      <!-- 搜索行 -->
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="5">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="6" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
          </v-row>
        </v-col>
      </v-row>
      <!-- 高级搜素行 -->
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="3" md="4">
                <SearcherKeyword
                  ref="purchase_code"
                  v-model="options.purchase_code"
                  @text="purchaseCodeKeyword = $event"
                  :label="$t('PurchasesDetail.Fields.purchase_code')"
                />
              </v-col>
              <v-col cols="12" sm="3" md="4">
                <SearcherKeyword
                  ref="stock_order_code"
                  v-model="options.stock_order_code"
                  @text="stockOrderCodeKeyword = $event"
                  :label="$t('StockOrderIN.Fields.stock_in_code')"
                />
              </v-col>
              <v-col cols="12" sm="3" md="4">
                <SearcherMaterials v-model="materialSelected" />
              </v-col>
              <v-col cols="12" sm="3" md="4">
                <SearcherStock v-model="stockSelected"/>
              </v-col>
              <v-col cols="12" sm="3" md="4">
                <SearcherSupplier v-model="supplierSelected"/>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <!-- 表格 -->
      <v-data-table
        :headers="headers"
        :items="stockIns"
        :loading="loading"
        :options.sync="options"
        :page.sync="paging.page"
        :server-items-length="paging.count"
        multi-sort
        class="elevation-0 px-6"
        fixed-header
        height="340px"
        :footer-props="footerProps"
      >
        <template v-slot:header.action="{ header }">
          {{ header.text }}
          <v-icon x-small>fa-sliders-h</v-icon>
        </template>
        <template v-slot:item.action="{ item }">
          <div class="d-flex mt-n2 mb-n3">
            <v-btn
              color="primary"
              dense
              @click="
                $emit('confirm', item);
                dialog = false;
              "
            >
              {{ $t("Shop.Fields.choose") }}
            </v-btn>
          </div>
        </template>
        <template v-slot:item.created="{ item }">
          <span class="text-no-wrap">
            {{ item.created | formatTime }}
          </span>
        </template>
      </v-data-table>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          outlined
          @click="dialog = false"
          class="elevation-1"
        >
          {{ $t("$vuetify.abandon") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio.vue";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import ReturnPurchases from "@/api/return_purchases";
import SearcherMaterials from "@/components/plugins/SearcherMaterials";
import SearcherStock from "@/components/plugins/SearcherStock";
import SearcherSupplier from "@/components/plugins/SearcherSupplier";

const defaultOptions = {
  datetimeRadioSelected: "purchase_created",
  purchase_created_time_from:
    new Date(new Date().getTime() - 7 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  purchase_created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  stock_created_time_from: "",
  stock_created_time_to: "",
  purchase_code: "",
  stock_order_code: "",
  disable_return: 0,
  stock_ids: [],
  supplier_ids: [],
  material_ids: [],
};

export default {
  name: "DialogPurchase",

  components: {
    SearcherDatetimeRangeWithRadio,
    SearcherKeyword,
    SearcherMaterials,
    SearcherStock,
    SearcherSupplier,
  },

  props: {
    value: Boolean,
    stock: Object,
    supplier: Object,
  },
  mounted() {
  },

  computed: {
    datetimeRadios: () => [
      {
        key: "purchase_created",
        text: i18n.t("returnPurchaseDetail.Fields.purchase_created"),
      },
      {
        key: "stock_created",
        text: i18n.t("returnPurchaseDetail.Fields.stock_created"),
      }
    ],
    headers() {
      return [

        {
          text: i18n.t("StockOrderIN.Fields.stock_in_code"),
          align: "start",
          sortable: false,
          value: "stock_order_code",
        },
        {
          text: i18n.t("PurchasesDetail.Fields.purchase_code"),
          align: "start",
          sortable: false,
          value: "purchase_code",
        },
        {
          text: i18n.t("PurchasesDetail.supplier"),
          align: "start",
          sortable: false,
          value: "supplier.supplier_name",
        },
        {
          text: i18n.t("PurchasesDetail.Fields.created"),
          align: "start",
          sortable: false,
          value: "created",
        },
        {
          text: i18n.t("Shop.Fields.action"),
          align: "start",
          sortable: false,
          value: "action",
        },
      ];
    },

    footerProps() {
      return Object.assign({}, i18n.t("$config.footer-props"), {
        itemsPerPageOptions: [this.itemsPerPageOptions],
      });
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings"]),
  },

  data: () => ({
    purchaseCodeKeyword: "",
    stockOrderCodeKeyword: "",
    dialog: false,
    options: JSON.parse(JSON.stringify(defaultOptions)),
    stockIns: [],
    paging: {},
    itemsPerPageOptions: 5,
    loginUserId: "",
    districtID: null,
    materialSelected: [],
    stockSelected: null,
    supplierSelected: null,
  }),

  watch: {
    value: {
      immediate: true, //初始赋值也调用
      handler() {
        this.dialog = this.value;
        if (this.dialog) {
          // 每次打开对话框重置查询
          this.resetSearch();
          if (this.stock.id) {
            this.stockSelected = JSON.parse(JSON.stringify(this.stock))
          }
          if (this.supplier.id) {
            this.supplierSelected = JSON.parse(JSON.stringify(this.supplier))
          }
        }
      },
    },
    dialog: {
      handler() {
        if (!this.dialog) {
          this.stockIns = [];
          this.paging = {};
          this.materialSelected = [];
          this.stockSelected = null;
          this.supplierSelected = null;
        }
        this.$emit("input", this.dialog);
      },
    },
    options: {
      deep: true,
      handler() {
        this.getStockInsFromAPI();
      },
    },
    materialSelected: {
      handler() {
        this.options.material_ids = this.materialSelected
          ? this.materialSelected.map((e) => {
              return e.id;
            })
          : [];
      },
    },
    stockSelected: {
      handler() {
        this.options.stock_ids = this.stockSelected
            ? [this.stockSelected.id]
            : [];
      },
    },
    supplierSelected: {
      handler() {
        this.options.supplier_ids = this.supplierSelected
            ? [this.supplierSelected.id]
            : [];
      },
    },
  },

  methods: {
    resizeHeader() {
      this.itemsPerPageOptions = window.innerHeight > 800 ? 10 : 5;
    },

    getStockInsFromAPI() {
      let option = JSON.parse(JSON.stringify(this.options));
      ReturnPurchases.getReturnPurchaseStockIns(option).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.stockIns = response.data.stock_ins;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        purchase_code: this.purchaseCodeKeyword,
      });
    },
    resetSearch() {
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      this.materialSelected = [];
      this.stockSelected = null;
      this.supplierSelected = null;
      [this.$refs.purchase_code, this.$refs.stock_order_code].forEach((k) => {
        if (k) k.clear();
      });
    },
  },
};
</script>
